// src/context/PlayerContext.mobile.js
import React, {
  createContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import vhiSwishSrc from "../music/tag/vhi swish.wav";
import { incrementPlay } from "../utils/playCounter";
import { trackBeatPlay, trackBeatThirtySeconds } from "../lib/commerceAnalytics";
import { getTrackCommerceFlags } from "../lib/trackCommerceCompatibility";
import { playableAudioUrl } from "../lib/playableAudio";
import usePlayerMediaSession from "../hooks/usePlayerMediaSession";
import {storefrontMediaPresentationBuildEnabled} from "../lib/storefrontMediaPresentationFeature";
import {claimOrdinaryAudio, isOrdinaryAudioCurrent, releaseOrdinaryAudio} from "../lib/ordinaryAudioPlayback";

export const MobilePlayerContext = createContext();

export function MobilePlayerProvider({ children }) {
  // Keep playback at normal pitch when BT devices switch sample-rate
  function forcePitchSafe(el) {
    if (!el) return;
    try {
      el.defaultPlaybackRate = 1;
      el.playbackRate = 1;
      el.preservesPitch = true;
      el.mozPreservesPitch = true;
      el.webkitPreservesPitch = true;
    } catch {
      // ignore
    }
  }

  /* ───────────── Refs & nodes ───────────── */
  const mainEl     = useRef(null);
  const preloadEl  = useRef(null);
  const ctx        = useRef(null);

  const masterGain = useRef(null);      // UI slider controls this
  const trackGain  = useRef(null);      // fades main player only
  const limiter    = useRef(null);
  const analyser   = useRef(null);

  const trackRefs  = useRef({});
  const swishBuf   = useRef(null);
  const swishNode  = useRef(null);
  const swishTmr   = useRef(null);

  const fadeToken  = useRef(0);
  const transitionToken = useRef(0);
  const isPlayingRef  = useRef(false);
  const sliderVolRef  = useRef(0.5);
  const animTmr    = useRef(null);
  const initDone   = useRef(false);

  const stopTimerRef       = useRef(null);   // ⏱ holds timer id
  const lastUserActionRef  = useRef(Date.now());
  const progressTrackedRef = useRef(new Set());

  const preloadedReadyRef  = useRef(false);

  // Worker for stall detection
  const watchdogWorkerRef  = useRef(null);

  /* ───────────── React state ───────────── */
  const [sliderVol, setSliderVol]   = useState(0.5);
  const [trackQueue, setTrackQueue] = useState([]);
  const [currentTrack, setCurrent]  = useState(null);
  const [isPlaying,    setPlaying]  = useState(false);
  const [isShuffle,    setShuffle]  = useState(false);
  const [animatingTrackId, setAnimatingTrackId] = useState(null);
  const [repeatMode, setRepeatMode] = useState("off");
  const isLoop = repeatMode === "all";

  const cycleRepeatMode = useCallback(() => {
    setRepeatMode(m => (m === "off" ? "all" : m === "all" ? "one" : "off"));
  }, []);

  useEffect(() => {
    if (mainEl.current) mainEl.current.loop = (repeatMode === "one");
  }, [repeatMode, currentTrack]);

  /* keep refs in sync with state */
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { sliderVolRef.current = sliderVol; }, [sliderVol]);

  /* ───────────── Which track comes next? ───────────── */
  const peekNext = useCallback(() => {
    if (!currentTrack) return null;
    const idx = trackQueue.findIndex(t => t.id === currentTrack.id);
    if (idx < 0) return null;
    if (isShuffle) {
      let r;
      do { r = Math.floor(Math.random() * trackQueue.length); }
      while (r === idx && trackQueue.length > 1);
      return trackQueue[r];
    }
    let n = idx + 1;
    if (n >= trackQueue.length) n = isLoop ? 0 : -1;
    return n >= 0 ? trackQueue[n] : null;
  }, [currentTrack, trackQueue, isShuffle, isLoop]);

  /* ───────────── Keep graph running (BT / visibility / stalls) ───────────── */
  const ensureRunning = useCallback(() => {
    const audio = mainEl.current;
    const ac    = ctx.current;
    if (!audio || !ac) return;

    const reallyWantsSound = isPlayingRef.current;
    const userPaused       = audio.paused;

    if (reallyWantsSound && ac.state !== "running") {
      ac.resume().catch(() => {});
    }

    if (reallyWantsSound && userPaused) {
      audio.play().then(() => {
        try {
          trackGain.current.gain.setValueAtTime(
            sliderVolRef.current,
            ac.currentTime
          );
        } catch {
          // ignore
        }
      }).catch(() => {
        // autoplay heuristics – ignore
      });
    }

    forcePitchSafe(audio);
  }, []);

  /* ───────────── Next-track preloading ───────────── */
  const isSafari =
    typeof navigator !== "undefined" &&
    /Safari/i.test(navigator.userAgent) &&
    !/Chrome/i.test(navigator.userAgent) &&
    !/CriOS/i.test(navigator.userAgent);

  const primeNextTrack = useCallback(() => {
    const next = peekNext();
    const el = preloadEl.current;
    preloadedReadyRef.current = false;
    if (!next || !el) return;

    // Always keep preloader silent
    el.muted = true;
    el.volume = 0;
    el.setAttribute("muted", "");
    el.preload = "auto";

    // Important: never "play/pause warmup" on Safari
    // (Safari can leak audio even if muted, especially with WebAudio)
    const nextAudioUrl = playableAudioUrl(next);
    if (!nextAudioUrl) return;
    if (el.src !== nextAudioUrl) {
      try { el.pause(); } catch {}
      el.src = nextAudioUrl;
      forcePitchSafe(el);
      el.load();
    }

    const onReady = () => {
      el.removeEventListener("canplaythrough", onReady);
      el.removeEventListener("loadeddata", onReady);
      preloadedReadyRef.current = true;
      try { el.currentTime = 0; } catch {}
    };

    // Use readiness events instead of play()
    el.addEventListener("canplaythrough", onReady, { once: true });
    el.addEventListener("loadeddata", onReady, { once: true });

    // Safari sometimes won't fire canplaythrough; loadeddata is a backup.
    // No play() calls here.
  }, [peekNext]);

  useEffect(() => { primeNextTrack(); },
    [currentTrack, trackQueue, isShuffle, isLoop, primeNextTrack]);

  /* ───────────── Graph setup ───────────── */
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    const AC = window.AudioContext || window.webkitAudioContext;
    ctx.current        = new AC();
    masterGain.current = ctx.current.createGain();
    trackGain.current  = ctx.current.createGain();
    limiter.current    = ctx.current.createDynamicsCompressor();
    analyser.current   = ctx.current.createAnalyser();

    /* limiter settings */
    limiter.current.threshold.setValueAtTime(-6, ctx.current.currentTime);
    limiter.current.knee     .setValueAtTime( 0, ctx.current.currentTime);
    limiter.current.ratio    .setValueAtTime(20, ctx.current.currentTime);
    limiter.current.attack   .setValueAtTime(0.003, ctx.current.currentTime);
    limiter.current.release  .setValueAtTime(0.25, ctx.current.currentTime);

    analyser.current.fftSize = 2048;

    /* master chain: trackGain → masterGain → limiter → analyser → destination */
    trackGain.current
      .connect(masterGain.current)
      .connect(limiter.current)
      .connect(analyser.current)
      .connect(ctx.current.destination);

    const hookUp = (el, toTrackGain = false) => {
      try {
        const src = ctx.current.createMediaElementSource(el);
        (toTrackGain ? trackGain.current : masterGain.current).connect
          ? src.connect(toTrackGain ? trackGain.current : masterGain.current)
          : src.connect(masterGain.current);
      } catch {
        // createMediaElementSource double-call guard
      }
    };

    hookUp(mainEl.current, true);

    // Keep preloader isolated from WebAudio graph
    if (preloadEl.current) {
      preloadEl.current.muted  = true;
      preloadEl.current.volume = 0;
    }

    // pitch safety on both elements
    forcePitchSafe(mainEl.current);
    forcePitchSafe(preloadEl.current);

    /* decode swish once */
    fetch(vhiSwishSrc)
      .then(r => r.arrayBuffer())
      .then(b => ctx.current.decodeAudioData(b))
      .then(d => (swishBuf.current = d))
      .catch(console.error);

    /* ── keep context alive across background / lock screen ── */
    const onVis        = () => ensureRunning();
    const onPageShow   = () => ensureRunning();
    const onStateChange = () => ensureRunning();
    const onDeviceChange = () => ensureRunning();

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pageshow",           onPageShow);
    ctx.current.addEventListener("statechange",   onStateChange);
    navigator.mediaDevices?.addEventListener?.("devicechange", onDeviceChange);

    const audioEl = mainEl.current;
    const onGlitch   = () => ensureRunning();
    const onRateSafe = () => forcePitchSafe(audioEl);

    if (audioEl) {
      audioEl.addEventListener("stalled", onGlitch);
      audioEl.addEventListener("waiting", onGlitch);
      audioEl.addEventListener("ratechange", onRateSafe);
      audioEl.addEventListener("play",       onRateSafe);
      audioEl.addEventListener("loadedmetadata", onRateSafe);

      audioEl.__onGlitch   = onGlitch;
      audioEl.__onRateSafe = onRateSafe;
    }

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pageshow",           onPageShow);
      ctx.current?.removeEventListener("statechange",  onStateChange);
      navigator.mediaDevices?.removeEventListener?.("devicechange", onDeviceChange);
      if (audioEl) {
        if (audioEl.__onGlitch) {
          audioEl.removeEventListener("stalled", audioEl.__onGlitch);
          audioEl.removeEventListener("waiting", audioEl.__onGlitch);
        }
        if (audioEl.__onRateSafe) {
          audioEl.removeEventListener("ratechange", audioEl.__onRateSafe);
          audioEl.removeEventListener("play",       audioEl.__onRateSafe);
          audioEl.removeEventListener("loadedmetadata", audioEl.__onRateSafe);
        }
        delete audioEl.__onGlitch;
        delete audioEl.__onRateSafe;
      }
      // Don't close ctx here – avoids React StrictMode double-mount issues
    };
  }, [ensureRunning]);

  /* ───────────── Worker-based stall watchdog ───────────── */
  useEffect(() => {
    if (typeof Worker === "undefined") return;
    if (!mainEl.current) return;

    const workerCode = `
      let last = null;
      self.onmessage = function(e) {
        const data = e.data || {};
        if (data.type === "TICK") {
          const payload = data.payload || {};
          const isPlaying   = !!payload.isPlaying;
          const currentTime = Number(payload.currentTime || 0);
          const now = Date.now();
          if (!last) {
            last = { t: now, currentTime };
            return;
          }
          const dt   = now - last.t;
          const dPos = currentTime - last.currentTime;
          last = { t: now, currentTime };
          if (isPlaying && dt > 4000 && dPos < 0.05) {
            self.postMessage({ type: "STALL_DETECTED" });
          }
        } else if (data.type === "RESET") {
          last = null;
        }
      };
    `;
    const blob = new Blob([workerCode], { type: "application/javascript" });
    const url  = URL.createObjectURL(blob);
    const worker = new Worker(url);
    watchdogWorkerRef.current = { worker, url };

    worker.onmessage = (e) => {
      if (e?.data?.type === "STALL_DETECTED") {
        ensureRunning();
      }
    };

    const tickId = window.setInterval(() => {
      const audio = mainEl.current;
      if (!audio) return;
      worker.postMessage({
        type: "TICK",
        payload: {
          isPlaying: isPlayingRef.current,
          currentTime: audio.currentTime || 0,
        },
      });
    }, 3000);

    return () => {
      clearInterval(tickId);
      if (watchdogWorkerRef.current) {
        watchdogWorkerRef.current.worker.terminate();
        URL.revokeObjectURL(watchdogWorkerRef.current.url);
        watchdogWorkerRef.current = null;
      }
    };
  }, [ensureRunning]);

  /* ───────────── Ramp helpers ───────────── */
  const rampParam = (param, target, ms = 250) =>
    new Promise(res => {
      const token = ++fadeToken.current;
      const now   = ctx.current.currentTime;
      let cur     = (typeof param.getValueAtTime === "function")
        ? param.getValueAtTime(now)
        : NaN;
      if (!Number.isFinite(cur)) cur = param.value;
      param.cancelScheduledValues(now);
      param.setValueAtTime(cur, now);
      if (ms === 0) {
        param.setValueAtTime(target, now);
        return res();
      }
      param.linearRampToValueAtTime(target, now + ms / 1000);
      setTimeout(() => token === fadeToken.current && res(), ms);
    });

  const rampMasterGainTo = (v, ms) => rampParam(masterGain.current.gain, v, ms);
  const rampTrackGainTo  = (v, ms) => rampParam(trackGain.current.gain , v, ms);

  /* ───────────── Swish loop ───────────── */
  const stopSwish = useCallback(() => {
    try { swishNode.current?.stop(); } catch {}
    swishNode.current = null;
    clearTimeout(swishTmr.current);
  }, []);

  const startSwish = useCallback(() => {
    if (!swishBuf.current) return;
    stopSwish();
    const loopOnce = () => {
      const n = ctx.current.createBufferSource();
      n.buffer = swishBuf.current;
      n.connect(masterGain.current);
      n.start();
      swishNode.current = n;
      swishTmr.current  = setTimeout(loopOnce, 40000);
    };
    loopOnce();
  }, [stopSwish]);

  /* ───────────── Fade-out main player only ───────────── */
  const fadeOutPlayback = useCallback(async (ms = 2000) => {
    if (!isPlaying) return;
    await rampTrackGainTo(0, ms);
    const audio = mainEl.current;
    audio.currentTime = 0;
    setPlaying(false);
    stopSwish();

    clearTimeout(stopTimerRef.current);
    stopTimerRef.current = setTimeout(() => {
      const now = Date.now();
      const age = now - lastUserActionRef.current;
      if (age >= 0) {
        mainEl.current?.pause();
        if (mainEl.current) mainEl.current.currentTime = 0;
      }
    }, 0);
  }, [isPlaying, rampTrackGainTo, stopSwish]);

  /* ───────────── Pure pause (keeps position) ───────────── */
  const pauseMainTrack = useCallback(async () => {
    if (!isPlaying) return;
    await rampTrackGainTo(0, 150);     // quick fade
    mainEl.current.pause();            // DON’T reset currentTime
    setPlaying(false);
    if (navigator.mediaSession) {
      navigator.mediaSession.playbackState = "paused";
    }
    stopSwish();
  }, [isPlaying, rampTrackGainTo, stopSwish]);

  /* ───────────── Resume after fade ───────────── */
  const resumeMainTrack = useCallback(async () => {
    if (!currentTrack || isPlaying) return;
    await ctx.current.resume().catch(() => {});

    forcePitchSafe(mainEl.current);
    const audio = mainEl.current;
    const version = ++transitionToken.current;
    const ordinaryToken = claimOrdinaryAudio("mobile-storefront-player", audio,
      {cancel: () => { transitionToken.current += 1; audio.pause(); setPlaying(false); }});
    trackGain.current.gain.setValueAtTime(0, ctx.current.currentTime);
    try { await audio.play(); } catch (error) {
      if (version === transitionToken.current) setPlaying(false);
      return false;
    }
    if (version !== transitionToken.current ||
        !isOrdinaryAudioCurrent("mobile-storefront-player", audio, ordinaryToken)) return;
    setPlaying(true);
    incrementPlay(currentTrack.id);
    if (navigator.mediaSession) {
      navigator.mediaSession.playbackState = "playing";
    }
    rampTrackGainTo(1, 150);                // fade main back in
    const flags = getTrackCommerceFlags(currentTrack);
    flags.original || flags.remix ? stopSwish() : startSwish();
    return true;
  }, [currentTrack, isPlaying, rampTrackGainTo, startSwish, stopSwish]);

  /* ───────────── Track transition (user-gesture safe) ───────────── */
  const transitionTo = useCallback((next, opts = {}) => {
    const shouldScrollIntoView = opts.scrollIntoView !== false;
    if (!next || next.id === currentTrack?.id) return Promise.resolve(false);

    const killPreloader = () => {
      const el = preloadEl.current;
      if (!el) return;
      try { el.pause(); } catch {}
      el.removeAttribute("src");
      el.load();
      el.muted = true;
      el.volume = 0;
      el.setAttribute("muted", "");
    };
    killPreloader();

    clearTimeout(animTmr.current);
    setAnimatingTrackId(next.id);
    animTmr.current = setTimeout(() => setAnimatingTrackId(null), 500);

    const audio = mainEl.current;
    const version = ++transitionToken.current;

    if (isPlaying) rampTrackGainTo(0, 250);

    audio.pause();
    audio.removeAttribute("src");
    audio.load();

    const nextAudioUrl = playableAudioUrl(next);
    if (!nextAudioUrl) return Promise.reject(new Error("playable-audio-unavailable"));
    audio.src = nextAudioUrl;
    audio.currentTime = 0;
    audio.load();

    ctx.current.resume().catch(() => {});
    forcePitchSafe(audio);
    const ordinaryToken = claimOrdinaryAudio("mobile-storefront-player", audio,
      {cancel: () => { transitionToken.current += 1; audio.pause(); setPlaying(false); }});

    return audio.play()
      .then(() => {
        if (version !== transitionToken.current ||
            !isOrdinaryAudioCurrent("mobile-storefront-player", audio, ordinaryToken)) {
          audio.pause(); return false;
        }
        if (shouldScrollIntoView) {
          trackRefs.current[next.id]?.scrollIntoView({ behavior:"smooth", block:"center" });
        }
        trackGain.current.gain.setValueAtTime(0, ctx.current.currentTime);
    rampTrackGainTo(1, 150);

        setCurrent(next);
        incrementPlay(next.id);
        trackBeatPlay(next);
        setPlaying(true);
        if (navigator.mediaSession) {
          navigator.mediaSession.playbackState = "playing";
        }
        const flags = getTrackCommerceFlags(next);
        flags.original || flags.remix ? stopSwish() : startSwish();
        primeNextTrack();
        return true;
      })
      .catch(err => {
        if (version !== transitionToken.current ||
            !isOrdinaryAudioCurrent("mobile-storefront-player", audio, ordinaryToken)) return false;
        console.warn("play() was blocked:", err);
        opts.onPlaybackError?.(err);
        setPlaying(false);
        trackGain.current.gain.setValueAtTime(0, ctx.current.currentTime);
        return false;
      });
  }, [currentTrack, isPlaying, rampTrackGainTo, startSwish, stopSwish, primeNextTrack]);

  const handleAnalyticsProgress = useCallback(() => {
    const seconds = Number(mainEl.current?.currentTime) || 0;
    const trackId = currentTrack?.id;
    if (!trackId || seconds < 30 || progressTrackedRef.current.has(trackId)) return;
    progressTrackedRef.current.add(trackId);
    trackBeatThirtySeconds(currentTrack);
  }, [currentTrack]);

  /* ───────────── Unified play handler (toggle-aware) ───────────── */
  const playMainTrack = useCallback((track, opts = {}) => {
    clearTimeout(stopTimerRef.current);
    lastUserActionRef.current = Date.now();

    if (!track) {
      return isPlaying ? pauseMainTrack() : resumeMainTrack();
    }

    if (currentTrack && track.id === currentTrack.id) {
      return isPlaying ? pauseMainTrack() : resumeMainTrack();
    }

    return transitionTo(track, opts);
  }, [currentTrack, isPlaying, pauseMainTrack, resumeMainTrack, transitionTo]);

  /* ───────────── Next / Prev, shuffle, etc. ───────────── */
  const pickNext = useCallback(dir => {
    clearTimeout(stopTimerRef.current);
    lastUserActionRef.current = Date.now();
    if (!currentTrack) return null;
    const idx = trackQueue.findIndex(t => t.id === currentTrack.id);
    if (idx < 0) return null;
    if (isShuffle) {
      let r;
      do { r = Math.floor(Math.random() * trackQueue.length); }
      while (r === idx && trackQueue.length > 1);
      return trackQueue[r];
    }
    let n = idx + dir;
    if (n >= trackQueue.length) n = isLoop ? 0 : -1;
    if (n < 0) n = isLoop ? trackQueue.length - 1 : -1;
    return n >= 0 ? trackQueue[n] : null;
  }, [currentTrack, trackQueue, isShuffle, isLoop]);

  const playNextTrack = useCallback(() => {
    clearTimeout(stopTimerRef.current);
    lastUserActionRef.current = Date.now();
    const t = pickNext(1);
    t ? transitionTo(t) : fadeOutPlayback(250);
  }, [pickNext, transitionTo, fadeOutPlayback]);

  const playPrevTrack = useCallback(() => {
    clearTimeout(stopTimerRef.current);
    lastUserActionRef.current = Date.now();
    const t = pickNext(-1);
    t ? transitionTo(t) : fadeOutPlayback(250);
  }, [pickNext, transitionTo, fadeOutPlayback]);

  /* ───────────── Slider controls master gain ───────────── */
  useEffect(() => { rampMasterGainTo(sliderVol, 50); }, [sliderVol, rampMasterGainTo]);

  usePlayerMediaSession({
    audioRef: mainEl,
    currentTrack,
    isPlaying,
    play: resumeMainTrack,
    pause: pauseMainTrack,
    previous: playPrevTrack,
    next: playNextTrack,
    enabled: storefrontMediaPresentationBuildEnabled(),
  });

  useEffect(() => () => {
    transitionToken.current += 1;
    if (mainEl.current) releaseOrdinaryAudio("mobile-storefront-player", mainEl.current, {resetSource: true});
  }, []);

  /* ───────────── Helpers exposed to Beat-Pack modal ───────────── */
  const connectElement = el => {
    try {
      const src = ctx.current.createMediaElementSource(el);
      src.connect(masterGain.current);    // preview bypasses trackGain
      return () => src.disconnect();
    } catch {
      return () => {};
    }
  };

  const seek = sec => { if (mainEl.current) mainEl.current.currentTime = sec; };
  const registerTrackRef = (id, el) =>
    el ? (trackRefs.current[id] = el) : delete trackRefs.current[id];

  /* ───────────── Provider ───────────── */
  return (
    <MobilePlayerContext.Provider value={{
      mainAudioRef: mainEl,
      analyserRef: analyser,

      trackQueue, updateTrackQueue: setTrackQueue,
      currentTrack,

      playMainTrack,
      pauseMainTrack,
      resumeMainTrack,
      playNextTrack, playPrevTrack,
      seek, fadeOutPlayback,

      isPlaying,
      globalVolume: sliderVol,
      setGlobalVolume: v => setSliderVol(Math.max(0, Math.min(1, v))),
      repeatMode,
      cycleRepeatMode,
      isLoop,
      toggleLoop   : () => setRepeatMode(m => (m === "all" ? "off" : "all")),
      isShuffle,
      toggleShuffle: () => setShuffle(s => !s),

      registerTrackRef,
      connectElement,
      animatingTrackId,
      startSwishLoop: startSwish,
      stopSwishLoop : stopSwish,
    }}>
      <audio
        ref={mainEl}
        preload="auto"
        crossOrigin="anonymous"
        playsInline
        {...{ "webkit-playsinline": "" }}
        onTimeUpdate={handleAnalyticsProgress}
      />
      <audio
        ref={preloadEl}
        preload="auto"
        muted
        crossOrigin="anonymous"
        playsInline
        {...{ "webkit-playsinline": "" }}
      />
      {children}
    </MobilePlayerContext.Provider>
  );
}
